"use client";

import {MouseEvent, MouseEventHandler, ReactNode} from "react";
import AppIcon, {SIZES, TYPES} from "@/app/components/atomic/AppIcon";

type AppButtonSize = "xs" | "sm" | "lg";
type AppButtonArrowPosition = "before" | "after" | "none";
// Arrow-circle CTAs (homepage/header) + flat pills from the "Mise aux couleurs"
// Figma (node 33:163). Flat themes render a single pill with an optional leading
// icon and ignore `size`/`arrowPosition`.
type AppButtonArrowTheme = "orange" | "whiteBlur";
type AppButtonFlatTheme =
    | "orangeFlat"
    | "white"
    | "orange-rounded"
    | "outline-rounded"
    | "callback"
    | "whiteDevis";
type AppButtonTheme = AppButtonArrowTheme | AppButtonFlatTheme;

interface AppButtonProps {
    onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
    href?: string;
    target?: "_blank" | "_self";
    children?: ReactNode;
    size?: AppButtonSize;
    arrowPosition?: AppButtonArrowPosition;
    wFull?: boolean;
    theme?: AppButtonTheme;
    icon?: string;
    iconPosition?: "before" | "after";
    iconType?: keyof typeof TYPES;
    loading?: boolean;
    dataTest?: string;
    disabled?: boolean;
    type?: "button" | "submit" | "reset";
    noWrap?: boolean;
    ariaPressed?: boolean;
    smoothScroll?: boolean;
    decorative?: boolean;
}

const THEMES: Record<AppButtonArrowTheme, { circle: string; text: string }> = {
    orange: {
        circle: "bg-pastel-orange text-white",
        text: "bg-rm-orange text-white title-look",
    },
    whiteBlur: {
        circle: "bg-pastel-blue text-pale backdrop-blur-[45px] border border-rm-blue",
        text: "bg-deep-blue/20 text-pale backdrop-blur-[45px] border border-rm-blue",
    },
};

// Single-pill themes. `--font-recoleta` is defined on <html> in V2, so the
// Recoleta variants render correctly on the V2 surfaces these are used on.
const FLAT_THEMES: Record<AppButtonFlatTheme, { className: string; iconSize: keyof typeof SIZES }> = {
    orangeFlat: {
        className: "h-[50px] gap-[10px] rounded-lg bg-rm-orange px-[10px] text-base font-normal leading-[22px] text-white hover:opacity-90",
        iconSize: "xl",
    },
    white: {
        // 1px et non 0.5px : un hairline sous-pixel disparaît sur un bord à position fractionnaire selon le ratio de pixels
        className: "h-[50px] gap-[10px] rounded-lg border border-[#d9d9d9] bg-white px-[10px] text-base font-normal leading-[22px] text-pale hover:border-rm-gray",
        iconSize: "xl",
    },
    "orange-rounded": {
        className: "rounded-xl bg-rm-orange p-[12px] text-base font-bold font-[family-name:var(--font-recoleta)] text-white hover:opacity-90",
        iconSize: "md",
    },
    "outline-rounded": {
        className: "gap-[10px] rounded-xl border border-rm-orange bg-white p-[12px] text-base font-bold font-[family-name:var(--font-recoleta)] text-rm-orange hover:bg-neutral",
        iconSize: "md",
    },
    callback: {
        className: "h-[51px] gap-[8px] rounded-2xl bg-[#edf9ed] p-[10px] text-xs font-bold text-[#509e50] hover:opacity-90",
        iconSize: "md",
    },
    whiteDevis: {
        className: "h-[51px] gap-[8px] rounded-2xl border border-[#f5f5f5] bg-white p-[10px] text-xs font-bold text-black hover:border-rm-gray-light",
        iconSize: "md",
    },
};

export default function AppButton({
                                         onClick,
                                         href,
                                         target = "_self",
                                         children,
                                         size = "sm",
                                         arrowPosition = "after",
                                         wFull = false,
                                         theme = "orange",
                                         icon,
                                         iconPosition = "before",
                                         iconType = "solid",
                                         loading = false,
                                         dataTest,
                                         disabled = false,
                                         type = "button",
                                         noWrap = false,
                                         ariaPressed,
                                         smoothScroll = false,
                                         decorative = false,
                                     }: AppButtonProps) {
    const flatTheme = (FLAT_THEMES as Record<string, { className: string; iconSize: keyof typeof SIZES }>)[theme];

    let content: ReactNode;
    let wrapperClass: string;

    if (flatTheme) {
        const flatIcon = icon ? <AppIcon iconClassName={icon} size={flatTheme.iconSize} type={iconType} /> : null;

        content = (
            <>
                {iconPosition === "before" && flatIcon}
                {children}
                {iconPosition === "after" && flatIcon}
            </>
        );
        wrapperClass = `inline-flex items-center justify-center whitespace-nowrap transition-all ${flatTheme.className} ${wFull ? "w-full" : ""} ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`;
    } else {
        const themeStyle = THEMES[theme as AppButtonArrowTheme];

        const circleClass = size === "xs"
            ? "h-9 w-9"
            : size === "sm"
                ? "h-12 w-12"
                : "h-14 w-14";

        const textClass = size === "xs"
            ? "h-9 text-sm"
            : size === "sm"
                ? "h-12 text-base"
                : "h-14 text-lg";

        const arrow = arrowPosition !== "none" ? (
            <div className={`rounded-full ${circleClass} ${themeStyle.circle} flex items-center justify-center shrink-0`}>
                <AppIcon
                    iconClassName={loading ? "fa-spinner-third" : icon ?? "fa-arrow-right"}
                    className={loading ? "animate-spin" : undefined}
                    size={size === "xs" ? "xl" : size === "sm" ? "2xl" : "4xl"}
                />
            </div>
        ) : null;

        const text = (
            <div className={`${textClass} ${themeStyle.text} font-semibold flex items-center ${size === "xs" ? "px-2 md:px-3" : "px-2 md:px-5"} rounded-xl w-full justify-center ${noWrap ? "whitespace-nowrap font-[family-name:var(--font-recoleta)]" : ""}`}>
                {children}
            </div>
        );

        content = arrowPosition === "before"
            ? <>{arrow}{text}</>
            : arrowPosition === "after"
                ? <>{text}{arrow}</>
                : text;

        wrapperClass = `flex ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:drop-shadow-md"} ${wFull ? "w-full" : noWrap ? "" : "max-w-[345px]"}`;
    }

    // A span, not a link or a button: lets a clickable card carry the CTA look without nesting two interactive elements
    if (decorative) {
        return <span className={wrapperClass}>{content}</span>;
    }

    if (href) {
        const isHashLink = smoothScroll && href.startsWith("#");

        const handleAnchorClick = (event: MouseEvent<HTMLAnchorElement>) => {
            if (isHashLink) {
                const target = document.getElementById(href.slice(1));
                if (target) {
                    event.preventDefault();
                    target.scrollIntoView({behavior: "smooth"});
                    if (window.location.hash !== href) {
                        history.pushState(null, "", href);
                    }
                }
            }
            onClick?.(event);
        };

        return (
            <a
                className={wrapperClass}
                href={href}
                target={target}
                onClick={handleAnchorClick}
                data-test={dataTest}
            >
                {content}
            </a>
        );
    }

    return (
        <button
            type={type}
            className={wrapperClass}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            data-test={dataTest}
            aria-pressed={ariaPressed}
        >
            {content}
        </button>
    );
}
